/**
 * CamposMascara — campos de texto com formato conhecido (CPF, telefone, PIS…).
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  CRIAÇÃO NOSSA. Se um campo com máscara se comportar de forma estranha,   ║
 * ║  ESTE arquivo é o primeiro suspeito — não o tema.                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * A MÁSCARA É DESENHADA, NUNCA DIGITADA — mesma regra do Valor Rápido: só
 * dígitos entram, os pontos e traços aparecem sozinhos. Quem digita não erra a
 * posição do separador porque a posição não é dele.
 *
 * O QUE VAI PARA O BANCO É O TEXTO FORMATADO, e isso é deliberado aqui: estes
 * campos não entram em conta nenhuma — CPF não se soma, telefone não se
 * subtrai. São para ler e ligar. Guardar formatado evita que cada tela invente
 * a própria formatação de leitura, e o `tel:` do link limpa o que precisa.
 *
 * RG NÃO TEM MÁSCARA, de propósito: o formato varia por estado (e alguns têm
 * letra). Máscara rígida ali recusaria RG válido — só se limita o tamanho.
 *
 * PORTADO DO PROJETO IRMÃO `webapp-rm-ops` (08/08/2026), sem alterar uma linha —
 * mesmo tema, mesma convenção `Custom`, mesmos campos de RH. Ao mexer aqui,
 * considere levar a mudança para lá também: ver [[pessoal-espelha-rm-ops]].
 *
 * TELEFONE: este app já tem o `CustomCampoTelefone` (bandeira + DDI, guarda em E.164) e
 * ele continua sendo o padrão nas telas de OS e cliente. O `CustomCampoTelefone`
 * daqui é o formato simples de RH, que grava o texto como se lê.
 */
import React from 'react';

/** `#` é dígito; o resto é desenhado. */
export function aplicarMascara(valor: string, padrao: string): string {
  const digitos = String(valor ?? '').replace(/\D/g, '');
  if (!digitos) return '';
  let saida = '';
  let i = 0;
  for (const c of padrao) {
    if (i >= digitos.length) break;
    if (c === '#') {
      saida += digitos[i];
      i += 1;
    } else {
      saida += c;
    }
  }
  return saida;
}

/** Telefone brasileiro: 8 ou 9 dígitos depois do DDD, e o formato muda no meio
 *  da digitação. Por isso não é uma máscara fixa. */
export function mascaraTelefone(valor: string): string {
  const d = String(valor ?? '').replace(/\D/g, '').slice(0, 11);
  if (d.length <= 10) return aplicarMascara(d, '(##) ####-####');
  return aplicarMascara(d, '(##) #####-####');
}

export const MASCARAS = {
  cpf: '###.###.###-##',
  pis: '###.#####.##-#',
  cep: '#####-###',
} as const;

/** Atributos que mantêm o gerenciador de senha fora do campo. Para o cofre,
 *  todo input de texto é candidato a login — o ícone dele nasce no canto e
 *  atrapalha. `autocomplete="off"` não basta: eles ignoram por política.
 *  Mesma solução do CustomCamposData e do CamposNumericos. */
export const SEM_COFRE = {
  autoComplete: 'off',
  'data-form-type': 'other' /* Dashlane */,
  'data-lpignore': 'true' /* LastPass */,
  'data-1p-ignore': 'true' /* 1Password */,
  'data-bwignore': 'true' /* Bitwarden */,
} as const;

type Props = {
  id?: string;
  label: string;
  valor: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Ícone à esquerda (`bi-whatsapp`, `bi-envelope`…). */
  icone?: string;
  /** Cor do ícone — `text-success` no WhatsApp, por exemplo. */
  corIcone?: string;
  inputMode?: 'text' | 'numeric' | 'email' | 'tel';
  type?: string;
  maxLength?: number;
  ajuda?: React.ReactNode;
  formatar?: (v: string) => string;
};

/** Base: rótulo flutuante, ícone opcional e cofre de senha fora. */
export function CustomCampoTexto({
  id,
  label,
  valor,
  onChange,
  disabled,
  placeholder,
  icone,
  corIcone,
  inputMode = 'text',
  type = 'text',
  maxLength,
  ajuda,
  formatar,
}: Props) {
  const campo = (
    <div className="form-floating">
      <input
        id={id}
        type={type}
        className="form-control"
        value={valor}
        onChange={(e) => onChange(formatar ? formatar(e.target.value) : e.target.value)}
        disabled={disabled}
        maxLength={maxLength}
        inputMode={inputMode}
        placeholder={placeholder ?? ' '}
        {...SEM_COFRE}
      />
      <label htmlFor={id}>{label}</label>
    </div>
  );

  return (
    <>
      {icone ? (
        <div className="input-group">
          <span className={`input-group-text ${corIcone ?? ''}`}>
            <i className={`bi ${icone}`}></i>
          </span>
          {campo}
        </div>
      ) : (
        campo
      )}
      {ajuda && <div className="form-text">{ajuda}</div>}
    </>
  );
}

export function CustomCampoCPF(p: Omit<Props, 'formatar' | 'inputMode' | 'maxLength'>) {
  return (
    <CustomCampoTexto
      {...p}
      inputMode="numeric"
      maxLength={14}
      formatar={(v) => aplicarMascara(v, MASCARAS.cpf)}
    />
  );
}

export function CustomCampoPIS(p: Omit<Props, 'formatar' | 'inputMode' | 'maxLength'>) {
  return (
    <CustomCampoTexto
      {...p}
      inputMode="numeric"
      maxLength={14}
      formatar={(v) => aplicarMascara(v, MASCARAS.pis)}
    />
  );
}

export function CustomCampoTelefone(p: Omit<Props, 'formatar' | 'inputMode' | 'maxLength'>) {
  return (
    <CustomCampoTexto {...p} inputMode="tel" maxLength={16} formatar={mascaraTelefone} />
  );
}
